import { useCallback, useEffect, useState } from "react";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Download, Trash2, RefreshCw } from "lucide-react";

export default function AdminClub() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    adminApi
      .get("/admin/club")
      .then((r) => setMembers(r.data.members))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const exportCsv = async () => {
    const token = localStorage.getItem("amyu_admin_token");
    const res = await fetch(`${API}/admin/club/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "amyu_cloud_club.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Supprimer ${m.name || m.email} du Cloud Club ?\n(Sa carte de fidélité restera intacte)`)) return;
    setDeletingId(m.id);
    try {
      await adminApi.delete(`/admin/club/${m.id}`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Membre supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId(null);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm(`⚠️ Supprimer TOUS les membres (${members.length}) du Cloud Club ?\nAction irréversible.`)) return;
    try {
      const r = await adminApi.delete("/admin/club");
      toast.success(`${r.data.deleted_count} membre(s) supprimé(s)`);
      fetchMembers();
    } catch {
      toast.error("Erreur");
    }
  };

  const syncFromLoyalty = async () => {
    try {
      const r = await adminApi.post("/admin/club/sync-from-loyalty");
      const { added, already_existed } = r.data;
      if (added === 0) {
        toast.success(`Tous les membres sont déjà synchronisés (${already_existed} déjà présents)`);
      } else {
        toast.success(`${added} membre(s) ajouté(s) depuis la fidélité !`);
      }
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de synchronisation");
    }
  };

  return (
    <div data-testid="admin-club-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl">Cloud Club</h1>
          <p className="text-amyu-muted mt-2">
            {members.length} membre{members.length > 1 ? "s" : ""} · chaque carte fidélité rejoint automatiquement le club
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={syncFromLoyalty}
            data-testid="admin-club-sync"
            title="Ajoute au Cloud Club tous les clients qui ont une carte fidélité mais qui n'y sont pas encore"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-amyu-pistachio text-amyu-ink hover:bg-amyu-pistachio/30 transition-colors"
          >
            <RefreshCw size={14} /> Sync fidélité
          </button>
          {members.length > 0 && (
            <button
              onClick={deleteAll}
              data-testid="admin-club-delete-all"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-coral/40 text-coral hover:bg-coral hover:text-white transition-colors"
            >
              <Trash2 size={14} /> Tout supprimer
            </button>
          )}
          <button
            onClick={exportCsv}
            data-testid="admin-club-export"
            className="btn-coral inline-flex items-center gap-2"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-coral" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center text-amyu-muted py-20 bg-white rounded-[28px] mt-6">
          Aucun membre pour l&apos;instant. Une carte fidélité créée = un membre ajouté automatiquement.
        </div>
      ) : (
        <div className="mt-6 bg-white rounded-[24px] overflow-x-auto">
          <table
            className="w-full min-w-[720px] text-sm"
            data-testid="admin-club-table"
          >
            <thead className="bg-amyu-cream">
              <tr>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-amyu-muted whitespace-nowrap">
                  Prénom
                </th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-amyu-muted whitespace-nowrap">
                  Email
                </th>
                <th className="text-center px-5 py-3 text-xs uppercase tracking-widest text-amyu-muted whitespace-nowrap">
                  Tampons gagnés
                </th>
                <th className="text-center px-5 py-3 text-xs uppercase tracking-widest text-amyu-muted whitespace-nowrap">
                  Tiramisus offerts
                </th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-amyu-muted whitespace-nowrap">
                  Inscrit le
                </th>
                <th className="px-5 py-3 whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-amyu/20" data-testid={`club-member-${m.id}`}>
                  <td className="px-5 py-3 font-medium whitespace-nowrap">{m.name || "—"}</td>
                  <td className="px-5 py-3 whitespace-nowrap">{m.email}</td>
                  <td className="px-5 py-3 text-center font-display text-lg text-coral">
                    {m.tampons_total || 0}
                  </td>
                  <td className="px-5 py-3 text-center font-display text-lg text-coral">
                    {m.tiramisus_offerts || 0}
                  </td>
                  <td className="px-5 py-3 text-amyu-muted whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => deleteMember(m)}
                      disabled={deletingId === m.id}
                      data-testid={`club-delete-${m.id}`}
                      title="Supprimer ce membre"
                      className="w-8 h-8 rounded-full inline-flex items-center justify-center text-amyu-muted hover:text-coral hover:bg-coral/10 disabled:opacity-40"
                    >
                      {deletingId === m.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
